// PartnershipTimeline — technical-drawing visual for "Strategic partnership"
// Uses only design tokens. Sharp corners, no shadows, one accent (primary).
// One-time build-in animation on enter; no loop.
import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

type Props = { bare?: boolean };

const mono = "font-mono uppercase tracking-[0.14em]";
const ease = [0.22, 1, 0.36, 1] as const;

// Build-in timing
const BAR_REVEAL = 0.7;     // duration per bar wipe
const BAR_STAGGER = 0.45;   // delay between bars
const BARS_START = 0.35;    // when bar wipes begin (after labels fade)

const PartnershipTimeline = ({ bare = false }: Props) => {
  const cornerBase = "absolute text-foreground/20 font-mono text-[10px] leading-none";
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const reduce = useReducedMotion();
  const show = inView || reduce;

  const fadeIn = (delay = 0) => ({
    initial: { opacity: 0, y: 4 },
    animate: show ? { opacity: 1, y: 0 } : {},
    transition: { duration: reduce ? 0 : 0.5, ease, delay: reduce ? 0 : delay },
  });

  const wipeIn = (i: number) => ({
    initial: reduce ? undefined : { clipPath: "inset(0% 100% 0% 0%)" },
    animate: show ? { clipPath: "inset(0% 0% 0% 0%)" } : {},
    transition: {
      duration: reduce ? 0 : BAR_REVEAL,
      ease,
      delay: reduce ? 0 : BARS_START + i * BAR_STAGGER,
    },
  });

  const arrowDelay = BARS_START + 2 * BAR_STAGGER + BAR_REVEAL; // after bars

  return (
    <div
      ref={ref}
      className={`relative ${
        bare ? "p-0" : "border border-border bg-[hsl(var(--seabed))] p-6 md:p-12"
      }`}
      style={{ borderRadius: 0 }}
    >
      {!bare && (
        <>
          <span className={`${cornerBase} top-2 left-2`}>+</span>
          <span className={`${cornerBase} top-2 right-2`}>+</span>
          <span className={`${cornerBase} bottom-2 left-2`}>+</span>
          <span className={`${cornerBase} bottom-2 right-2`}>+</span>
        </>
      )}

      <div className="relative">
        {/* Headline */}
        <motion.p
          {...fadeIn(0)}
          className={`${mono} text-center text-[11px] md:text-[12px] text-primary mb-6`}
        >
          Built for the models, vendors, and regulations still ahead
        </motion.p>

        <div className="relative">
          {/* Segment labels (above) */}
          <div className="flex w-full">
            {[
              { w: "w-[28%]", t: "OPERATIONALIZE" },
              { w: "w-[44%]", t: "GOVERN" },
              { w: "w-[28%]", t: "SCALE" },
            ].map((l, i) => (
              <div key={l.t} className={`${l.w} text-center`}>
                <motion.span
                  {...fadeIn(0.1 + i * 0.05)}
                  className={`${mono} inline-block text-[11px] md:text-[12px] text-foreground`}
                >
                  {l.t}
                </motion.span>
              </div>
            ))}
          </div>

          {/* Segment bars — staggered L→R wipe-in */}
          <div className="relative mt-3 flex w-full h-20 md:h-24">
            <motion.div
              {...wipeIn(0)}
              className="w-[28%] h-full"
              style={{ backgroundColor: "hsl(var(--primary))" }}
            />
            <div
              className="absolute top-[-8px] bottom-[-8px]"
              style={{
                left: "28%",
                borderLeft: "1px dashed hsl(var(--foreground) / 0.25)",
              }}
            />
            <motion.div
              {...wipeIn(1)}
              className="w-[44%] h-full overflow-hidden relative"
            >
              <motion.div
                className="absolute left-0 right-0"
                style={{
                  top: "-12.7279px",
                  bottom: "-12.7279px",
                  backgroundImage:
                    "repeating-linear-gradient(45deg, hsl(var(--primary) / 0.55) 0 1.5px, transparent 1.5px 9px)",
                  willChange: "transform",
                }}
                animate={
                  reduce || !show
                    ? undefined
                    : { y: [0, -12.7279] }
                }
                transition={{
                  duration: 1.6,
                  ease: "linear",
                  repeat: Infinity,
                  delay: BARS_START + 1 * BAR_STAGGER + BAR_REVEAL,
                }}
              />
            </motion.div>
            <div
              className="absolute top-[-8px] bottom-[-8px]"
              style={{
                left: "72%",
                borderLeft: "1px dashed hsl(var(--foreground) / 0.25)",
              }}
            />
            <motion.div
              {...wipeIn(2)}
              className="w-[28%] h-full"
              style={{ border: "1px dashed hsl(var(--primary) / 0.5)" }}
            />
          </div>

          {/* Arrow line — fades in after bars */}
          <motion.div
            {...fadeIn(arrowDelay)}
            className="relative mt-3 h-3 w-full"
          >
            <span
              className="absolute left-0 top-1/2 -translate-y-1/2 block rounded-full"
              style={{
                width: 8,
                height: 8,
                backgroundColor: "hsl(var(--primary))",
              }}
            />
            <span
              className="absolute left-0 right-2 top-1/2 -translate-y-1/2 block"
              style={{
                height: 1,
                backgroundColor: "hsl(var(--primary) / 0.6)",
              }}
            />
            <span
              className="absolute right-0 top-1/2 -translate-y-1/2 block"
              style={{
                width: 0,
                height: 0,
                borderTop: "4px solid transparent",
                borderBottom: "4px solid transparent",
                borderLeft: "7px solid hsl(var(--primary) / 0.7)",
              }}
            />
            {[0.28, 0.72].map((p) => (
              <span
                key={p}
                className="absolute top-1/2"
                style={{
                  left: `${p * 100}%`,
                  width: 1,
                  height: 5,
                  marginTop: -2,
                  backgroundColor: "hsl(var(--foreground) / 0.3)",
                }}
              />
            ))}
          </motion.div>

          {/* Range labels (below) */}
          <div className="mt-3 flex w-full">
            {[
              { w: "w-[28%]", t: "FRAMEWORK" },
              { w: "w-[44%]", t: "OPERATIONAL REALITY" },
              { w: "w-[28%]", t: "NEXT" },
            ].map((r, i) => (
              <div key={r.t} className={`${r.w} text-center`}>
                <motion.span
                  {...fadeIn(arrowDelay + 0.15 + i * 0.05)}
                  className={`${mono} inline-block text-[10px] md:text-[11px] text-primary`}
                >
                  {r.t}
                </motion.span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnershipTimeline;
